if (!customElements.get('mm-stock-alert')) {
  customElements.define(
    'mm-stock-alert',
    class MmStockAlert extends HTMLElement {
      constructor() {
        super();
        this.pending = false;
        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        document.addEventListener('variant:change', this.onVariantChange);
        this.autoSubscribe();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('variant:change', this.onVariantChange);
      }

      get submitButton() {
        return this.closest('.product-form__buttons')?.querySelector('.product-form__submit');
      }

      onClick(event) {
        if (event.target.closest('[data-stock-alert-submit]')) this.subscribe();
        else if (event.target.closest('[data-stock-alert-cancel]')) this.cancel();
        else if (event.target.closest('[data-stock-alert-reload]')) window.location.reload();
      }

      // El HTML de la sección que llega al cambiar de variante trae ya el bloque (y su firma) de la variante nueva
      onVariantChange(event) {
        const { html, sectionId } = event.detail;
        if (sectionId !== this.dataset.sectionId) return;

        const source = html.getElementById(this.id);
        if (!source) return;

        // modal-dialog se mueve al body al conectarse: el de la variante anterior hay que retirarlo
        document.querySelectorAll('modal-dialog[data-owner="' + this.id + '"]').forEach((modal) => modal.remove());

        [...this.attributes]
          .filter((attr) => attr.name.startsWith('data-'))
          .forEach((attr) => this.removeAttribute(attr.name));
        [...source.attributes]
          .filter((attr) => attr.name.startsWith('data-'))
          .forEach((attr) => this.setAttribute(attr.name, attr.value));
        this.toggleAttribute('hidden', source.hasAttribute('hidden'));
        this.innerHTML = source.innerHTML;
        this.pending = false;

        this.submitButton?.classList.toggle('hidden', !this.hidden);
      }

      // Muestra solo los elementos que declaran el estado en data-state-show
      setState(state) {
        this.dataset.state = state;
        this.querySelectorAll('[data-state-show]').forEach((element) => {
          element.hidden = !element.dataset.stateShow.split(' ').includes(state);
        });
      }

      // Tras el login el cliente vuelve con ?variant=ID&notify=1: se activa el aviso sin pulsar de nuevo
      autoSubscribe() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('notify') !== '1' || !this.dataset.signature) return;
        // Escritorio y móvil renderizan un bloque cada uno: solo actúa el visible
        if (!this.getClientRects().length) return;
        if (params.get('variant') !== this.dataset.variantGid?.split('/').pop()) return;

        params.delete('notify');
        const query = params.toString();
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (query ? '?' + query : '') + window.location.hash
        );
        this.subscribe();
      }

      post(url, body) {
        return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify(body),
        });
      }

      async subscribe() {
        if (this.pending) return;
        this.pending = true;
        this.setState('sending');

        const { endpoint, variantGid, customerId, timestamp, signature, locale, country } = this.dataset;

        try {
          const response = await this.post(endpoint, { variantGid, customerId, timestamp, signature, locale, country });
          // Si mientras tanto cambió la variante, el bloque ya es otro
          if (this.dataset.variantGid !== variantGid) return;
          await this.handleSubscribeResponse(response);
        } catch (error) {
          if (this.dataset.variantGid === variantGid) this.setState('error');
        } finally {
          this.pending = false;
        }
      }

      async handleSubscribeResponse(response) {
        // 201 alta creada, 200 ya estaba apuntado
        if (response.status === 201 || response.status === 200) {
          this.setState('subscribed');
          return;
        }
        // La firma caduca a los 15 minutos: hay que recargar la página para obtener una nueva
        if (response.status === 401) {
          this.setState('expired');
          return;
        }
        if (response.status === 429) {
          this.setState('rate_limit');
          return;
        }
        // 400 payload inválido o variante con stock, 404 variante inexistente, 503 función desactivada:
        // reintentar no cambia nada, el aviso no procede y se vuelve al "Agotado"
        if (response.status === 400 || response.status === 404 || response.status === 503) {
          this.setState('unavailable');
          return;
        }
        this.setState('error');
      }

      async cancel() {
        if (this.pending) return;
        this.pending = true;

        const { deleteEndpoint, variantGid, customerId, deleteTimestamp, deleteSignature } = this.dataset;

        try {
          const response = await this.post(deleteEndpoint, {
            variantGid,
            customerId,
            timestamp: deleteTimestamp,
            signature: deleteSignature,
          });
          if (this.dataset.variantGid !== variantGid) return;
          // 200 eliminada, 404 ya no estaba apuntado: en ambos casos vuelve al estado inicial
          if (response.status === 200 || response.status === 404) {
            this.setState('idle');
          } else if (response.status === 401) {
            this.setState('expired');
          } else {
            this.setState('cancel_error');
          }
        } catch (error) {
          if (this.dataset.variantGid === variantGid) this.setState('cancel_error');
        } finally {
          this.pending = false;
        }
      }
    }
  );
}
